/**
 * StatCardGroupElement — gotowy klocek Kreatora: grupa kafelków KPI.
 *
 * „Gotowy element do umieszczenia w module" (wymóg produktowy): wdrożeniowiec
 * przeciąga go z palety Kreatora do layoutu modułu i konfiguruje karty
 * (etykieta + wartość + kolor). Renderuje się przez wspólny `StatCard` — ten sam
 * wygląd co dedykowane ekrany HR. Wartości są statyczne (konfigurowane w edytorze);
 * podpięcie pod dane rekordu/metryki = możliwy Etap 2.
 *
 * Settings shape:
 *   { columns?: 2 | 3 | 4, cards: { label: string; value: string; color?: StatCardColor }[] }
 */

import { Dropdown } from '@/components/Dropdown';
import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';
import { StatCard, type StatCardColor } from '@/components/ui/StatCard';
// Element layoutu — `render`/`propertiesEditor` są wołane jak zwykłe funkcje (nie jako
// komponenty), więc hook `useTranslation` byłby niebezpieczny. Używamy standalone `i18n.t`
// (ten sam wzorzec co components/tabs/tenant-tab-meta.ts).
import i18n from '@/i18n';

interface CardCfg {
  label: string;
  value: string;
  color?: StatCardColor;
}

// `label` = klucz i18n; tłumaczenie w miejscu użycia (i18n.t) — mapa jest na poziomie modułu.
const COLOR_OPTIONS: { value: StatCardColor; label: string }[] = [
  { value: 'accent', label: 'hr.kpiCards.colors.accent' },
  { value: 'indigo', label: 'hr.kpiCards.colors.indigo' },
  { value: 'blue', label: 'hr.kpiCards.colors.blue' },
  { value: 'teal', label: 'hr.kpiCards.colors.teal' },
  { value: 'green', label: 'hr.kpiCards.colors.green' },
  { value: 'amber', label: 'hr.kpiCards.colors.amber' },
  { value: 'red', label: 'hr.kpiCards.colors.red' },
  { value: 'purple', label: 'hr.kpiCards.colors.purple' },
  { value: 'gray', label: 'hr.kpiCards.colors.gray' },
];

const COLS_CLASS: Record<number, string> = {
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
};

function readSettings(raw: Record<string, unknown> | undefined): {
  columns: number;
  cards: CardCfg[];
} {
  const s = raw ?? {};
  const columns = [2, 3, 4].includes(Number(s.columns)) ? Number(s.columns) : 3;
  const cards = Array.isArray(s.cards) ? (s.cards as CardCfg[]) : [];
  return { columns, cards };
}

export const StatCardGroupElement: LayoutElementDefinition = {
  type: 'stat_card_group',
  category: 'tresc',
  isContainer: false,
  label: i18n.t('hr.kpiCards.title'),
  icon: 'LayoutGrid',
  defaultSettings: () => ({
    columns: 3,
    cards: [
      { label: i18n.t('hr.kpiCards.metric', { n: 1 }), value: '0', color: 'indigo' },
      { label: i18n.t('hr.kpiCards.metric', { n: 2 }), value: '0', color: 'green' },
      { label: i18n.t('hr.kpiCards.metric', { n: 3 }), value: '0', color: 'amber' },
    ],
  }),
  propertiesEditor: ({ settings, onChange }) => {
    const { columns, cards } = readSettings(settings);

    const updateCard = (i: number, patch: Partial<CardCfg>) => {
      const next = cards.map((c, idx) => (idx === i ? { ...c, ...patch } : c));
      onChange({ cards: next });
    };
    const addCard = () =>
      onChange({
        cards: [...cards, { label: i18n.t('hr.kpiCards.newMetric'), value: '0', color: 'accent' }],
      });
    const removeCard = (i: number) => onChange({ cards: cards.filter((_, idx) => idx !== i) });

    return (
      <div className="space-y-3" data-testid="stat-card-group-properties-editor">
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">
            {i18n.t('hr.kpiCards.columns')}
          </label>
          <Dropdown
            value={String(columns)}
            onChange={(v) => onChange({ columns: Number(v) })}
            className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
            options={[
              { value: '2', label: '2' },
              { value: '3', label: '3' },
              { value: '4', label: '4' },
            ]}
          />
        </div>

        <div className="space-y-2">
          <span className="block text-xs font-medium text-text-secondary">
            {i18n.t('hr.kpiCards.cards')}
          </span>
          {cards.map((c, i) => (
            <div key={i} className="rounded-md border border-border-primary p-2">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs text-text-muted">
                  {i18n.t('hr.kpiCards.card', { n: i + 1 })}
                </span>
                <button
                  type="button"
                  onClick={() => removeCard(i)}
                  className="text-xs font-medium text-danger-600 hover:underline"
                >
                  {i18n.t('hr.kpiCards.remove')}
                </button>
              </div>
              <div className="space-y-2">
                <input
                  type="text"
                  value={c.label ?? ''}
                  onChange={(e) => updateCard(i, { label: e.target.value })}
                  placeholder={i18n.t('hr.kpiCards.label')}
                  className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={c.value ?? ''}
                    onChange={(e) => updateCard(i, { value: e.target.value })}
                    placeholder={i18n.t('hr.kpiCards.value')}
                    className="w-1/2 rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
                  />
                  <Dropdown
                    value={c.color ?? 'accent'}
                    onChange={(v) => updateCard(i, { color: v as StatCardColor })}
                    className="w-1/2 rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
                    options={COLOR_OPTIONS.map((o) => ({ value: o.value, label: i18n.t(o.label) }))}
                  />
                </div>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addCard}
            className="w-full rounded-md border border-dashed border-border-primary py-1.5 text-sm text-text-secondary hover:bg-bg-tertiary"
          >
            {i18n.t('hr.kpiCards.addCard')}
          </button>
        </div>
      </div>
    );
  },
  render: ({ element, mode }) => {
    const { columns, cards } = readSettings(element.settings);
    if (cards.length === 0) {
      return mode === 'builder' ? (
        <div className="rounded-md border border-dashed border-border-primary p-4 text-center text-sm text-text-muted">
          {i18n.t('hr.kpiCards.empty')}
        </div>
      ) : null;
    }
    return (
      <div
        className={`grid grid-cols-2 gap-3 ${COLS_CLASS[columns] ?? COLS_CLASS[3]}`}
        data-element-type="stat_card_group"
        data-testid={`stat-card-group-${element.id}`}
      >
        {cards.map((c, i) => (
          <StatCard
            key={i}
            label={c.label || '—'}
            value={c.value || '—'}
            iconColor={c.color ?? 'accent'}
          />
        ))}
      </div>
    );
  },
};

export default StatCardGroupElement;
